import { Component, OnDestroy, OnInit, WritableSignal, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { PendingReviewsBanner } from '../../shared/components/pending-reviews-banner/pending-reviews-banner';
import { Toast } from '../../shared/services/toast';
import { Toast as ToastComponent } from '../../shared/components/toast/toast';
import { Button } from '../../shared/components/button/button';
import {
  PolicyFlow as PolicyFlowService,
  PolicyFlowGraph,
  RebuildStatus,
} from '../../core/services/policy-flow';
import { PolicyReviewTable } from './policy-review-table/policy-review-table';
import { FlowView } from './flow-view/flow-view';
import { PolicyGraph } from './policy-graph/policy-graph';

type Tab = 'policies' | 'flows' | 'graph';

const POLL_MS = 4000;

@Component({
  selector: 'app-policy-flow',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Sidebar,
    Navbar,
    ToastComponent,
    Button,
    PendingReviewsBanner,
    PolicyReviewTable,
    FlowView,
    PolicyGraph,
  ],
  templateUrl: './policy-flow.html',
  styleUrl: './policy-flow.css',
})
export class PolicyFlowPage implements OnInit, OnDestroy {
  private service = inject(PolicyFlowService);
  private toast = inject(Toast);

  graph = signal<PolicyFlowGraph | null>(null);
  status = signal<RebuildStatus | null>(null);

  activeTab = signal<Tab>('policies');
  isLoadingGraph = signal(false);
  showConfirm = signal(false);

  pendingPolicyIds = signal<Set<string>>(new Set<string>());
  pendingFlowIds = signal<Set<string>>(new Set<string>());

  private pollHandle: ReturnType<typeof setInterval> | null = null;
  private prevState: string | null = null;

  isRunning = computed(() => this.status()?.state === 'Running');
  policies = computed(() => this.graph()?.policies ?? []);
  flows = computed(() => this.graph()?.flows ?? []);
  governs = computed(() => this.graph()?.governs ?? []);

  ngOnInit(): void {
    this.loadStatus(true);
    this.loadGraph();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  setTab(tab: Tab) {
    this.activeTab.set(tab);
  }

  // ---- data loads ----

  loadGraph() {
    this.isLoadingGraph.set(true);
    this.service.graph().subscribe({
      next: g => {
        this.isLoadingGraph.set(false);
        this.graph.set(g);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoadingGraph.set(false);
        const msg =
          err.status === 502
            ? 'AI service is unreachable — try again shortly.'
            : this.msgOf(err, 'Failed to load the policy/flow graph.');
        this.toast.show(msg, 'error');
      },
    });
  }

  private loadStatus(startPollingIfRunning = false) {
    this.service.status().subscribe({
      next: s => {
        const prev = this.prevState;
        this.status.set(s);
        this.prevState = s.state;

        if (s.state === 'Running') {
          if (startPollingIfRunning) this.startPolling();
        } else {
          this.stopPolling();
          // Fire once, only on the transition into a terminal state.
          if (prev === 'Running' && s.state === 'Succeeded') {
            this.toast.show('Rebuild finished. Refreshing graph…', 'success');
            this.loadGraph();
          } else if (prev === 'Running' && s.state === 'Failed') {
            this.toast.show(s.error || 'Rebuild failed.', 'error');
          }
        }
      },
      error: (err: HttpErrorResponse) => {
        this.toast.show(this.msgOf(err, 'Failed to read rebuild status.'), 'error');
      },
    });
  }

  // ---- rebuild ----

  askRebuild() {
    if (this.isRunning()) return;
    this.showConfirm.set(true);
  }

  cancelRebuild() {
    this.showConfirm.set(false);
  }

  confirmRebuild() {
    this.showConfirm.set(false);
    this.service.rebuild().subscribe({
      next: () => {
        this.toast.show('Rebuild started.', 'success');
        this.startPolling();
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 409) {
          // A rebuild is already running for this tenant — just follow it.
          this.toast.show('A rebuild is already running — tracking its progress.', 'success');
          this.startPolling();
        } else {
          this.toast.show(this.msgOf(err, 'Could not start the rebuild.'), 'error');
        }
      },
    });
  }

  private startPolling() {
    // Reflect Running immediately so the button disables without waiting a tick.
    this.prevState = 'Running';
    this.status.update(s => (s ? { ...s, state: 'Running' } : s));
    if (this.pollHandle) return;
    this.pollHandle = setInterval(() => this.loadStatus(), POLL_MS);
  }

  private stopPolling() {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  }

  // ---- review (optimistic) ----

  onReviewPolicy(evt: { id: string; review: boolean }) {
    this.setPending(this.pendingPolicyIds, evt.id, true);
    this.patchPolicy(evt.id, evt.review);

    this.service.setReview('policy', evt.id, evt.review).subscribe({
      next: () => this.setPending(this.pendingPolicyIds, evt.id, false),
      error: (err: HttpErrorResponse) => {
        this.patchPolicy(evt.id, !evt.review); // revert
        this.setPending(this.pendingPolicyIds, evt.id, false);
        this.toast.show(this.msgOf(err, 'Could not update the policy.'), 'error');
      },
    });
  }

  onReviewFlow(evt: { id: string; review: boolean }) {
    this.setPending(this.pendingFlowIds, evt.id, true);
    this.patchFlow(evt.id, evt.review);

    this.service.setReview('flow', evt.id, evt.review).subscribe({
      next: () => this.setPending(this.pendingFlowIds, evt.id, false),
      error: (err: HttpErrorResponse) => {
        this.patchFlow(evt.id, !evt.review); // revert
        this.setPending(this.pendingFlowIds, evt.id, false);
        this.toast.show(this.msgOf(err, 'Could not update the flow.'), 'error');
      },
    });
  }

  private patchPolicy(id: string, review: boolean) {
    this.graph.update(g =>
      g ? { ...g, policies: g.policies.map(p => (p.policy_id === id ? { ...p, review } : p)) } : g
    );
  }

  private patchFlow(id: string, review: boolean) {
    this.graph.update(g =>
      g ? { ...g, flows: g.flows.map(f => (f.flow_id === id ? { ...f, review } : f)) } : g
    );
  }

  private setPending(set: WritableSignal<Set<string>>, id: string, on: boolean) {
    set.update(s => {
      const next = new Set(s);
      on ? next.add(id) : next.delete(id);
      return next;
    });
  }

  private msgOf(err: HttpErrorResponse, fallback: string): string {
    // Error envelopes carry no `data`; the user-facing text is in `message`.
    return err?.error?.message || err?.message || fallback;
  }
}
